// src/pages/company/CompanyDetailPage.js
import React, { useEffect, useState } from "react"
import {
  Row, Col, Card, CardHeader, CardBody, Spinner, Alert,
} from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import { useNavigate, useParams, Link } from "react-router-dom"
import axios from "axios"
import { API } from "../../api/urls"
import { fetchCompanyLogo, fetchCompanyFavicon } from "../../api/requests/company-requests"
import {
  COMPANY_TYPE_LABEL_MAP,
  COMPANY_TYPE_BADGE_COLORS,
} from "../../constants/companyTypes"
import { AssignSubscriptionModal } from "../modals/subscription/AssignSubscriptionModal"
import "../../assets/css/page-common.css"
import BackButton from "../../components/Common/BackButton";
import { formatDaysLeft, formatLicenseDate } from "../../utils/subscriptionDates";

const NAVY = "#221C5D"
const ORANGE = "#E56A22"
const GREEN = "#71C30B"
const RED = "#dc3545"

const formatDate = (d) => formatLicenseDate(d)

const formatNumber = (num) => {
  if (num === undefined || num === null || num === "") return "-"
  const n = Number(num)
  return Number.isNaN(n) ? "-" : n.toFixed(5)
}

const getWorkingAreaText = (area) => {
  const map = {
    INFORMATION_TECHNOLOGY: "Bilişim ve Yazılım",
    MANUFACTURING: "Üretim ve İmalat",
    RETAIL: "Perakende Satış",
    HEALTHCARE: "Sağlık ve Tıp",
    FINANCE: "Finans ve Sigorta",
    EDUCATION: "Eğitim Hizmetleri",
    AUTOMOTIVE: "Otomotiv Sektörü",
    FOOD_AND_BEVERAGE: "Gıda ve İçecek",
    CONSTRUCTION: "İnşaat Sektörü",
    LOGISTICS: "Lojistik ve Nakliye",
    ENERGY: "Enerji Üretimi ve Dağıtımı",
    ECOMMERCE: "E-Ticaret",
    CONSULTING: "Danışmanlık",
    OTHER: "Diğer Sektörler",
  }
  return map[area] || area || "-"
}

// SubscriptionDetailPage.jsx'teki map ile aynı küme — eksik değerler (PENDING,
// CANCELLED, UPGRADED) rozet üstünde ham İngilizce enum olarak görünüyordu.
const STATUS_BADGE = {
  ACTIVE:    { label: "Aktif",          color: GREEN,    bg: "rgba(113,195,11,.1)" },
  TRIAL:     { label: "Deneme",         color: ORANGE,   bg: "rgba(229,106,34,.1)" },
  PENDING:   { label: "Ödeme Bekliyor", color: ORANGE,   bg: "rgba(229,106,34,.1)" },
  EXPIRED:   { label: "Sona Erdi",      color: "#6c757d", bg: "rgba(108,117,125,.1)" },
  CANCELLED: { label: "İptal",          color: "#E07A7A", bg: "rgba(224,122,122,.1)" },
  UPGRADED:  { label: "Yükseltilmiş",   color: NAVY,     bg: "rgba(34,28,93,.1)" },
}

const StatusBadge = ({ status }) => {
  const cfg = STATUS_BADGE[status] || { label: status || "-", color: "#B0B7C3", bg: "rgba(176,183,195,.1)" }
  return (
    <span
      className="badge border-0"
      style={{
        borderRadius: 999,
        padding: ".35rem .6rem",
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: ".78rem",
        border: `1px solid ${cfg.color}20`,
      }}
    >
      {cfg.label}
    </span>
  )
}

const ActiveBadge = ({ active, label }) => {
  const color = active ? GREEN : "#6c757d"
  const bg = active ? "rgba(113,195,11,.1)" : "rgba(108,117,125,.1)"
  return (
    <span
      className="badge border-0"
      style={{
        borderRadius: 999,
        padding: ".35rem .6rem",
        backgroundColor: bg,
        color,
        fontWeight: 600,
        fontSize: ".78rem",
        border: `1px solid ${color}20`,
      }}
    >
      {label}
    </span>
  )
}

const CompanyDetailPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [company, setCompany] = useState(null)
  const [assignModalOpen, setAssignModalOpen] = useState(false)

  const [logoPreview, setLogoPreview] = useState(null)
  const [faviconPreview, setFaviconPreview] = useState(null)

  const fetchData = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await axios.get(API.admin_company(id), { headers: {} })
      if (data?.statusCode === 200) {
        setCompany(data.data)
      } else {
        setError("Firma bilgileri getirilemedi")
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/login")
        return
      }
      setError(err.response?.data?.message || "Firma bilgileri alınırken hata oluştu")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    document.title = "Firma Detayları | Tahsilat360"

    const fetchImages = async () => {
      try {
        const resL = await fetchCompanyLogo(id)
        if (resL.ok && resL.headers.get("Content-Type")?.startsWith("image/")) {
          const blob = await resL.blob()
          setLogoPreview(URL.createObjectURL(blob))
        }
      } catch { /* logo yok */ }
      try {
        const resF = await fetchCompanyFavicon(id)
        if (resF.ok && resF.headers.get("Content-Type")?.startsWith("image/")) {
          const blob = await resF.blob()
          setFaviconPreview(URL.createObjectURL(blob))
        }
      } catch { /* favicon yok */ }
    }

    fetchData().then(fetchImages)
  }, [id]) // eslint-disable-line

  const daysColor = (d) => d <= 7 ? RED : d <= 30 ? ORANGE : GREEN

  if (loading) {
    return (
      <div className="page-content text-center p-5">
        <Spinner size="lg" color="primary" />
        <h6 className="mt-3 text-muted">Yükleniyor...</h6>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content">
        <Alert color="danger" className="rounded-4">
          <i className="bx bx-error-circle me-2" />
          {error}
          <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={fetchData}>
            Tekrar Dene
          </button>
        </Alert>
      </div>
    )
  }

  return (
    <div className="page-content page-common">
      <div className="container-fluid">

        {/* ── Page Header ── */}
        <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <BackButton />
            <div>
              <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                {company?.companyName || "Firma"}
              </h5>
              <span className="text-muted" style={{ fontSize: ".82rem" }}>
                Firma Detay Sayfası
              </span>
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            {/* Kullanıcılar sayfasındaki mevcut firma filtresini query param ile açar. */}
            <Link
              to={`/users?companyId=${id}`}
              className="btn btn-light btn-rounded d-flex align-items-center gap-1 px-3"
              style={{ height: 34, fontSize: ".8rem" }}
            >
              <i className="bx bx-group" style={{ fontSize: 16 }} /> Kullanıcıları Gör
            </Link>
            <button
              className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
              style={{ height: 34, fontSize: ".8rem" }}
              onClick={() => setAssignModalOpen(true)}
            >
              <i className="bx bx-package" style={{ fontSize: 16 }} /> Abonelik Tanımla
            </button>
            {!!company && (
              <Link
                to={`/companies/${company.id}/edit`}
                className="btn btn-rounded d-flex align-items-center gap-1 px-3"
                style={{ height: 34, fontSize: ".8rem", background: "#475569", borderColor: "#475569", color: "#fff" }}
              >
                <i className="bx bx-edit-alt" style={{ fontSize: 16 }} /> Düzenle
              </Link>
            )}
          </div>
        </div>

        {/* ── Temel Bilgiler ── */}
        <Card className="detail-card mb-3">
          <CardHeader>
            <div className="section-title">
              <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                <i className="bx bx-buildings" />
              </div>
              Temel Bilgiler
            </div>
          </CardHeader>
          <CardBody>
            <Row>
              <Col xs={6} md={3} className="info-field">
                <div className="info-label">Firma Adı</div>
                <div className="info-value">{company?.companyName || "-"}</div>
              </Col>
              <Col xs={6} md={3} className="info-field">
                <div className="info-label">Firma Kodu</div>
                <div className="info-value">{company?.companyCode || "-"}</div>
              </Col>
              <Col xs={6} md={3} className="info-field">
                <div className="info-label">Durum</div>
                <div className="mt-1">
                  <ActiveBadge active={company?.active} label={company?.active ? "Aktif" : "Pasif"} />
                </div>
              </Col>
              <Col xs={6} md={3} className="info-field">
                <div className="info-label">Firma Tipi</div>
                <div className="mt-1">
                  {company?.companyType ? (
                    <span
                      className="badge text-white"
                      style={{
                        backgroundColor: COMPANY_TYPE_BADGE_COLORS[company.companyType] || "#6c757d",
                        borderRadius: 999,
                        padding: ".35rem .6rem",
                        fontSize: ".78rem",
                        fontWeight: 600,
                      }}
                    >
                      {COMPANY_TYPE_LABEL_MAP[company.companyType] || company.companyType}
                    </span>
                  ) : (
                    <span className="info-value">-</span>
                  )}
                </div>
              </Col>
            </Row>
            <div style={{ borderTop: "1px solid #f0f2f5", paddingTop: ".75rem", marginTop: ".5rem" }}>
              <Row>
                <Col xs={6} md={3} className="info-field">
                  <div className="info-label">Alt Domain</div>
                  <div className="info-value">{company?.subdomain || "-"}</div>
                </Col>
                <Col xs={6} md={3} className="info-field">
                  <div className="info-label">Domain</div>
                  <div className="info-value">{company?.domain || "-"}</div>
                </Col>
                <Col xs={6} md={3} className="info-field">
                  <div className="info-label">Faaliyet Alanı</div>
                  <div className="info-value">{getWorkingAreaText(company?.workingArea)}</div>
                </Col>
                <Col xs={6} md={3} className="info-field">
                  <div className="info-label">Oluşturulma</div>
                  <div className="info-value">{formatDate(company?.createdAt)}</div>
                </Col>
              </Row>
            </div>
          </CardBody>
        </Card>

        {/* ── İstatistik Kutucukları ── */}
        <div className="stat-chip-row mb-3">
          {[
            {
              label: "Abonelik Durumu",
              value: company?.subscriptionStatus
                ? (STATUS_BADGE[company.subscriptionStatus]?.label || company.subscriptionStatus)
                : "-",
              color: STATUS_BADGE[company?.subscriptionStatus]?.color || "#B0B7C3",
              bg: STATUS_BADGE[company?.subscriptionStatus]?.bg || "rgba(176,183,195,.1)",
              icon: "bx-package",
            },
            {
              label: "Mevcut Paket",
              value: company?.currentPlanName || "-",
              color: NAVY,
              bg: "rgba(34,28,93,.1)",
              icon: "bx-crown",
            },
            {
              label: "Kalan Gün",
              value: formatDaysLeft(company?.daysRemaining, company?.subscriptionEndDate),
              color: company?.daysRemaining != null ? daysColor(company.daysRemaining) : "#B0B7C3",
              bg: company?.daysRemaining != null
                ? (company.daysRemaining <= 7 ? "rgba(220,53,69,.1)" : company.daysRemaining <= 30 ? "rgba(229,106,34,.1)" : "rgba(113,195,11,.1)")
                : "rgba(176,183,195,.1)",
              icon: "bx-calendar",
            },
            {
              label: "Abonelik Bitiş",
              value: formatDate(company?.subscriptionEndDate),
              color: "#475569",
              bg: "rgba(71,85,105,.1)",
              icon: "bx-time-five",
            },
          ].map((s, i) => (
            <div key={i} className="stat-chip">
              <div className="stat-icon" style={{ background: s.bg, color: s.color }}>
                <i className={`bx ${s.icon}`} />
              </div>
              <div className="stat-number" style={{ color: s.color, fontSize: s.value?.length > 12 ? "1rem" : "1.35rem" }}>
                {s.value}
              </div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── İletişim & Fatura ── */}
        <Row className="mb-3 g-3">
          <Col lg={6}>
            <Card className="detail-card h-100">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(113,195,11,.1)", color: GREEN }}>
                    <i className="bx bx-phone" />
                  </div>
                  İletişim Bilgileri
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col sm={6} className="info-field">
                    <div className="info-label">İletişim Kişisi</div>
                    <div className="info-value">{company?.contactFullName || "-"}</div>
                  </Col>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Telefon</div>
                    <div className="info-value">{company?.phoneNumber || "-"}</div>
                  </Col>
                </Row>
                <Row>
                  <Col sm={6} className="info-field">
                    <div className="info-label">E-posta</div>
                    <div className="info-value" style={{ fontSize: ".85rem" }}>
                      {company?.contactEmail || "-"}
                    </div>
                  </Col>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Adres</div>
                    <div className="info-value" style={{ fontSize: ".85rem" }}>
                      {company?.address || "-"}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="detail-card h-100">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(229,106,34,.1)", color: ORANGE }}>
                    <i className="bx bx-receipt" />
                  </div>
                  Fatura Bilgileri
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Vergi Numarası</div>
                    <div className="info-value">{company?.invoiceTaxNumber || "-"}</div>
                  </Col>
                  <Col sm={6} className="info-field">
                    <div className="info-label">TC Kimlik No</div>
                    <div className="info-value">{company?.invoiceTcNumber || "-"}</div>
                  </Col>
                </Row>
                <Row>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Vergi Dairesi</div>
                    <div className="info-value">{company?.invoiceTaxOffice || "-"}</div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Lisans & Abonelik ── */}
        <Row className="mb-3 g-3">
          <Col lg={6}>
            <Card className="detail-card h-100">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                    <i className="bx bx-key" />
                  </div>
                  Lisans Bilgileri
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Başlangıç Tarihi</div>
                    <div className="info-value">{formatDate(company?.licenseStartDate)}</div>
                  </Col>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Bitiş Tarihi</div>
                    <div className="info-value">{formatDate(company?.licenseExpiryDate)}</div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="detail-card h-100">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(113,195,11,.1)", color: GREEN }}>
                    <i className="bx bx-package" />
                  </div>
                  Abonelik Bilgileri
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Durum</div>
                    <div className="mt-1">
                      {company?.subscriptionStatus
                        ? <StatusBadge status={company.subscriptionStatus} />
                        : <span className="info-value">-</span>}
                    </div>
                  </Col>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Kalan Gün</div>
                    <div className="info-value" style={{
                      color: company?.daysRemaining != null ? daysColor(company.daysRemaining) : "#1e293b",
                      fontSize: "1.15rem",
                    }}>
                      {formatDaysLeft(company?.daysRemaining, company?.subscriptionEndDate)}
                    </div>
                  </Col>
                </Row>
                <div style={{ borderTop: "1px solid #f0f2f5", paddingTop: ".75rem", marginTop: ".25rem" }}>
                  <Row>
                    <Col sm={6} className="info-field">
                      <div className="info-label">Mevcut Paket</div>
                      <div className="info-value" style={{ color: NAVY }}>{company?.currentPlanName || "-"}</div>
                    </Col>
                    <Col sm={6} className="info-field">
                      <div className="info-label">Abonelik Bitiş</div>
                      <div className="info-value">{formatDate(company?.subscriptionEndDate)}</div>
                    </Col>
                  </Row>
                  {company?.subscriptionStatus === "TRIAL" && (
                    <Row>
                      <Col sm={6} className="info-field">
                        <div className="info-label">Deneme Başlangıç</div>
                        <div className="info-value">{formatDate(company?.trialStartDate)}</div>
                      </Col>
                      <Col sm={6} className="info-field">
                        <div className="info-label">Deneme Bitiş</div>
                        <div className="info-value">{formatDate(company?.trialEndDate)}</div>
                      </Col>
                    </Row>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Döviz Kuru Bilgileri ── */}
        <Card className="detail-card mb-3">
          <CardHeader>
            <div className="section-title">
              <div className="section-icon" style={{ background: "rgba(229,106,34,.1)", color: ORANGE }}>
                <i className="bx bx-dollar-circle" />
              </div>
              Döviz Kuru Bilgileri
            </div>
          </CardHeader>
          <CardBody className="pt-0">
            <div className="table-responsive rounded-3" style={{ border: "1px solid #e2e8f0" }}>
              <Table className="table table-hover detail-table mb-0">
                <Thead>
                  <Tr>
                    <Th>Para Birimi</Th>
                    <Th>Ekleme Oranı (%)</Th>
                    <Th>Final Kur (TL)</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {[
                    { label: "ABD Doları (USD)", markup: company?.usdMarkupRate, rate: company?.finalUsdRate },
                    { label: "Euro (EUR)", markup: company?.eurMarkupRate, rate: company?.finalEurRate },
                    { label: "İngiliz Sterlini (GBP)", markup: company?.gbpMarkupRate, rate: company?.finalGbpRate },
                  ].map((row, i) => (
                    <Tr key={i}>
                      <Td><span className="fw-semibold" style={{ color: "#1e293b" }}>{row.label}</span></Td>
                      <Td className="text-muted">{row.markup ?? "-"}</Td>
                      <Td className="fw-semibold">{formatNumber(row.rate)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        {/* ── Görseller ── */}
        {(logoPreview || faviconPreview) && (
          <Card className="detail-card mb-3">
            <CardHeader>
              <div className="section-title">
                <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                  <i className="bx bx-image" />
                </div>
                Görseller
              </div>
            </CardHeader>
            <CardBody>
              <Row className="g-3">
                <Col md={6}>
                  <div style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    padding: "1.5rem",
                    textAlign: "center",
                  }}>
                    <div className="info-label mb-2">Logo</div>
                    {logoPreview ? (
                      <img src={logoPreview} alt="logo" style={{ maxHeight: 100, objectFit: "contain" }} className="img-fluid" />
                    ) : (
                      <div className="text-muted">
                        <i className="bx bx-image" style={{ fontSize: 36, opacity: .3 }} />
                        <div className="mt-1" style={{ fontSize: ".82rem" }}>Logo bulunamadı</div>
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    padding: "1.5rem",
                    textAlign: "center",
                  }}>
                    <div className="info-label mb-2">Favicon</div>
                    {faviconPreview ? (
                      <img src={faviconPreview} alt="favicon" style={{ maxHeight: 60, objectFit: "contain" }} className="img-fluid" />
                    ) : (
                      <div className="text-muted">
                        <i className="bx bx-globe" style={{ fontSize: 36, opacity: .3 }} />
                        <div className="mt-1" style={{ fontSize: ".82rem" }}>Favicon bulunamadı</div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {/* ── Sözleşme Metinleri ── */}
        {(company?.cancellationReturnPrivacy || company?.personalDataPolicy ||
          company?.serviceContract || company?.onlineCollectionContract) && (
          <Card className="detail-card mb-3">
            <CardHeader>
              <div className="section-title">
                <div className="section-icon" style={{ background: "rgba(108,117,125,.1)", color: "#475569" }}>
                  <i className="bx bx-file" />
                </div>
                Sözleşme Metinleri
              </div>
            </CardHeader>
            <CardBody>
              {[
                { title: "İptal / İade Koşulları & Gizlilik", text: company?.cancellationReturnPrivacy },
                { title: "Kişisel Veriler Politikası", text: company?.personalDataPolicy },
                { title: "Hizmet Sözleşmesi", text: company?.serviceContract },
                { title: "Online Tahsilat Sözleşmesi", text: company?.onlineCollectionContract },
              ].filter(c => c.text).map((c, i) => (
                <div
                  key={i}
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 12,
                    padding: "1rem 1.25rem",
                    marginBottom: i < 3 ? ".75rem" : 0,
                  }}
                >
                  <div className="info-label mb-2">{c.title}</div>
                  <pre style={{
                    fontFamily: "monospace",
                    fontSize: 12,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    margin: 0,
                    color: "#334155",
                  }}>
                    {c.text}
                  </pre>
                </div>
              ))}
            </CardBody>
          </Card>
        )}

      </div>

      <AssignSubscriptionModal
        isOpen={assignModalOpen}
        toggle={() => setAssignModalOpen(false)}
        company={company}
        onSuccess={() => fetchData()}
      />
    </div>
  )
}

export default CompanyDetailPage
